import { useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { CoreBox, CoreTypography } from '@youscience/core';
import { Editor as TinyMCEEditor } from 'tinymce';

import { sxStyles, StyledEditorWrap } from './RichTextEditor.styles';

interface TextEditorProps {
  title: string;
  value: string;
  onChange?: (content: string) => void;
  wordsLimit: number;
  plugins?: string[];
  toolbar?: string;
}

const DEFAULT_PLUGINS = ['autoresize', 'wordcount'];
const DEFAULT_TOOLBAR = 'undo redo | bold italic underline';

export const RichTextEditor = (props: TextEditorProps) => {
  const { title, value = '', onChange, wordsLimit, plugins = [], toolbar = DEFAULT_TOOLBAR } = props;

  const characters = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const handleEditorChange = (content: string, editor: TinyMCEEditor) => {
    const wordsCount = editor.plugins.wordcount?.body.getWordCount() as number;

    if (wordsCount > wordsLimit) return;

    (characters.current as HTMLDivElement).innerText = `${wordsCount}/${wordsLimit} words`;

    if (onChange) {
      onChange(content);
    }
  };

  return (
    <CoreBox>
      <CoreTypography data-testid="editorTitle" variant="subtitle2" component="h4">
        {title}
      </CoreTypography>

      <StyledEditorWrap isFocused={isFocused}>
        <Editor
          apiKey={process.env.VITE_RICH_TEXT_EDITOR_API_KEY}
          value={value}
          init={{
            min_height: 100,
            menubar: false,
            statusbar: false,
            plugins: [...DEFAULT_PLUGINS, ...plugins],
            toolbar,
            content_style: 'body { font-family:Roboto,sans-serif; font-size:16px }',
            formats: { underline: { inline: 'u', exact: true } },
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onEditorChange={handleEditorChange}
        />

        <CoreBox data-testid="wordCount" sx={sxStyles.wordCount} ref={characters}>
          {`${0}/${wordsLimit} words`}
        </CoreBox>
      </StyledEditorWrap>
    </CoreBox>
  );
};
